import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, BellRing, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function AlertsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', service_id: 'all', metric: 'status', operator: 'down_for', threshold: 2, window_minutes: 5, severity: 'high', enabled: true });

  const load = async () => {
    const [{ data: a }, { data: r }, { data: s }] = await Promise.all([
      api.get('/alerts'), api.get('/alert-rules'), api.get('/services'),
    ]);
    setAlerts(a); setRules(r); setServices(s);
  };
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  const ack = async (id) => { try { await api.post(`/alerts/${id}/ack`); load(); } catch (e) { toast.error(t('error')); } };
  const del = async (id) => { try { await api.delete(`/alerts/${id}`); load(); } catch (e) { toast.error(t('error')); } };

  const createRule = async () => {
    try {
      await api.post('/alert-rules', { ...form, service_id: form.service_id === 'all' ? null : form.service_id });
      toast.success(t('success')); setOpen(false); load();
    } catch (e) { toast.error(t('error')); }
  };
  const toggleRule = async (r) => { await api.patch(`/alert-rules/${r.id}`, { enabled: !r.enabled }); load(); };
  const delRule = async (id) => { await api.delete(`/alert-rules/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('alerts_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{alerts.filter(a => !a.acknowledged).length} active</p>
        </div>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-alerts-active">{t('alerts_active')}</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-alerts-rules">{t('alerts_rules')}</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card className="bg-card border border-border">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                <th className="px-4 py-3">When</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Severity</th><th className="px-4 py-3">Message</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className={`border-b border-border/60 ${a.acknowledged ? 'opacity-60' : ''} hover:bg-accent/40`} data-testid={`alert-row-${a.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[hsl(var(--muted-foreground))]">{a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ''}</td>
                    <td className="px-4 py-3">{a.service_name}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${a.severity === 'high' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>{a.severity}</span></td>
                    <td className="px-4 py-3">{a.message}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {canEdit && !a.acknowledged && <Button variant="ghost" size="sm" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}><Check className="h-3.5 w-3.5" /></Button>}
                        {a.acknowledged && <span className="text-[10px] text-emerald-400 px-1.5">{t('acknowledged')}</span>}
                        {canEdit && <Button variant="ghost" size="sm" onClick={() => del(a.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {alerts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <div className="flex justify-end mb-3">
            {canEdit && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button data-testid="alerts-create-rule-button"><Plus className="h-4 w-4 mr-2" />{t('alerts_new_rule')}</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('alerts_new_rule')}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-form-name" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label>Service</Label>
                        <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All services</SelectItem>
                            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1"><Label>Metric</Label>
                        <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="status">status</SelectItem>
                            <SelectItem value="latency">latency</SelectItem>
                            <SelectItem value="error_rate">error_rate</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1"><Label>Operator</Label>
                        <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="down_for">down_for</SelectItem>
                            <SelectItem value="gt">gt</SelectItem>
                            <SelectItem value="lt">lt</SelectItem>
                            <SelectItem value="eq">eq</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1"><Label>Threshold</Label><Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: parseFloat(e.target.value || 0) })} /></div>
                      <div className="space-y-1"><Label>Window (min)</Label><Input type="number" value={form.window_minutes} onChange={(e) => setForm({ ...form, window_minutes: parseInt(e.target.value || 5, 10) })} /></div>
                      <div className="space-y-1"><Label>Severity</Label>
                        <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">low</SelectItem><SelectItem value="medium">medium</SelectItem><SelectItem value="high">high</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button><Button onClick={createRule} data-testid="rule-form-submit">{t('create')}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <Card className="bg-card border border-border">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Condition</th><th className="px-4 py-3">Severity</th><th className="px-4 py-3">Enabled</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`alerts-rule-row-${r.id}`}>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{services.find((s) => s.id === r.service_id)?.name || 'All'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.metric} {r.operator} {r.threshold}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${r.severity === 'high' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>{r.severity}</span></td>
                    <td className="px-4 py-3"><Switch checked={r.enabled} onCheckedChange={() => toggleRule(r)} disabled={!canEdit} /></td>
                    <td className="px-4 py-3 text-right">{canEdit && <Button variant="ghost" size="sm" onClick={() => delRule(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>}</td>
                  </tr>
                ))}
                {rules.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
